import { LinkRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  List,
  ListItem,
  Pagination,
  Paper,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactNode, useContext } from "react";
import { Link } from "react-router-dom";
import { usePaginationState } from "../../../App/Components/Table/State/PaginationState";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { useCampaignFormFieldsQuery, useSingleCampaign } from "../../../App/Queries/CampaignQueries";
import { useCandidateProfileCampaignResponsesQuery } from "../../../App/Queries/CandidateQueries";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import { IFormQueryResponse, ISelectedJobAlert } from "../../../App/Typings/Campaigns";
import { ITableColumn } from "../../../App/Typings/TableColumn";
import createSlug from "../../../App/Utilities/createSlug";
import { formatPhone } from "../../../App/Utilities/formatUtilities";
import { CandidateContext } from "../../CandidateProfile";

/**
 * @description Creates a card for each campaign that the candidate has responded to and displays the responses in a table
 * @returns Campaign Response card
 */
export function CampaignResponses({ sx }: { sx?: SxProps }) {
  const { candidate } = useContext(CandidateContext);
  const {
    getters: { page, getQueryString },
    handlers: { handleChangePage },
  } = usePaginationState({ pageSize: 1 }, `candidate-campaign-responses-${candidate.id}`);
  const { data: responses, isFetched } = useCandidateProfileCampaignResponsesQuery(
    candidate.id,
    getQueryString(),
  );

  return isFetched ? (
    <Fade in={isFetched} timeout={500}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ...sx }}>
        {responses && responses.items.length > 0 ? (
          <Paper data-test={`campaign-response-${responses.items[0].campaignId}`} sx={{ p: 2 }}>
            <CampaignResponse key={responses.items[0].id} response={responses.items[0]} />
            {responses.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Pagination
                  data-test="campaign-responses-pagination"
                  count={responses.totalPages}
                  page={Math.max(page, 1)}
                  onChange={handleChangePage}
                />
              </Box>
            )}
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Typography id={`campaign-noresponses`} data-test={`campaign-noresponses`}>
              No responses found!
            </Typography>
          </Paper>
        )}
      </Box>
    </Fade>
  ) : (
    <Box sx={{ minHeight: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress />
    </Box>
  );
}

function CampaignResponse({ response }: { response: IFormQueryResponse }) {
  const { data: campaign } = useSingleCampaign(response.campaignId);
  const { data: fieldList = [] } = useCampaignFormFieldsQuery(response.campaignId, response.formId);
  const contactInfoAccess = useFeaturePermission(AppFeatureLookup.ProfileViewCandidateContactInformation);
  const { getUserAdjustedDate } = useUserProfileStore();

  const rowNames: ITableColumn[] = fieldList
    .map((field) => ({
      label: field.label + (field.archived ? " (archived)" : ""),
      propName: field.id,
      order: field.order,
    }))
    .filter((field) =>
      field.propName.includes("phone") || field.propName.includes("email") ? contactInfoAccess : true,
    );

  return (
    <>
      <Stack
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        direction={{ xs: "column", md: "row" }}
      >
        <Typography variant="h4" sx={{ maxWidth: { xs: "100%", md: "calc(100% - 16ch)" } }}>
          Form: {response.campaignName}
        </Typography>
        {campaign?.isDeleted ? (
          <i data-test={`campaign-${campaign.id}-deleted`}>This form has been deleted.</i>
        ) : (
          <Button
            data-test={`link-campaign-${response.campaignId}-profile`}
            variant="outlined"
            component={Link}
            endIcon={<LinkRounded />}
            to={`/leadcapture/forms/${response.campaignId}`}
            sx={{ width: "auto" }}
          >
            View Form Profile
          </Button>
        )}
      </Stack>
      <Table>
        <TableHead
          sx={{
            borderBottomWidth: "2px",
            borderStyle: "solid",
            borderColor: "transparent",
            borderBottomColor: "primary.main",
          }}
        >
          <TableRow>
            <TableCell>Field</TableCell>
            <TableCell>Response</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowNames.map((column, index) => {
            const label = response.fieldLabels?.[column.propName] ?? column.label;
            let value = response.candidateResponse[column.propName] as ReactNode;
            if (column.propName === "dateAdded") {
              const date = getUserAdjustedDate(response.dateSubmitted);

              value = `${date.format("MM/DD/YYYY")} ${date.format("h:mm A")}`;
            }
            if (column.propName === "interestPicker") {
              value = getSelectedJobAlertsDisplay(response.selectedJobAlerts);
            }
            if (Array.isArray(value)) {
              value = value.join(", ");
            }
            if (column.propName.includes("phoneNumber")) {
              value = formatPhone(value as string);
            }

            const isLastRow = index === rowNames.length - 1;
            return (
              <TableRow key={label} data-test={`field-${createSlug(label)}`}>
                <TableCell
                  variant="head"
                  sx={{
                    fontWeight: "bold",
                    maxWidth: "20em",
                    width: 0,
                    minWidth: "12em",
                    borderBottomWidth: isLastRow ? 0 : "1px",
                  }}
                >
                  <>{label}</>
                </TableCell>
                <TableCell sx={{ borderBottomWidth: isLastRow ? 0 : "1px" }}>
                  <>{value}</>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}

function getSelectedJobAlertsDisplay(selectedAdGroups: ISelectedJobAlert[] | undefined) {
  if (!selectedAdGroups || selectedAdGroups.length === 0) {
    return "No Interests Selected";
  }
  return (
    <List sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1, p: 0 }}>
      {selectedAdGroups.map((item) => {
        const itemSlug = item.display.replace(/ /g, "-").replace(/,/g, "").toLowerCase();
        return (
          <ListItem key={itemSlug} data-test={`interestpicker-${itemSlug}`} sx={{ p: 0, width: "auto" }}>
            <Chip label={item.display} />
          </ListItem>
        );
      })}
    </List>
  );
}
